import * as React from "react"

import { ChurchNavBadgeVariant, type ChurchNavSection } from "@/features/dashboard/model/church-nav"
import { useTranslations } from "@/i18n"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import {
  FinanceOnboardingStatus,
  useFinanceOnboardingProgressQuery,
} from "@/features/finance/services/finance-onboarding-api"
import { useFinanceSetupQuery } from "@/features/finance/services/finance-setup-api"

/** The Finance section's canonical url, and the items the gate injects. */
const FINANCE_SECTION_URL = "/finance"
/**
 * The parish's org-wide finance section. It is a SIBLING of /finance in the parish nav, so collapsing
 * /finance left this one standing with its whole submenu — overview, chart, funds, reports — on a parish
 * that had not opened its books. Every one of those is a page about money that does not exist yet.
 *
 * Removed rather than collapsed: the Configure entry already sits under /finance one section above, and two
 * of them would be noise. It returns whole the moment the parish activates.
 */
const GLOBAL_FINANCE_SECTION_URL = "/global-finance"
const CONFIGURE_ITEM: ChurchNavSection["items"][number] = {
  titleKey: "churchNav.sections.finance.configure",
  url: "/finance/configure",
  enabled: true,
}
/** Shown alongside the normal Finance nav once finance is usable but the guided flow is still resumable. */
const CONTINUE_ITEM: ChurchNavSection["items"][number] = {
  titleKey: "churchNav.sections.finance.continueSetup",
  url: "/finance/configure",
  enabled: true,
}

/**
 * Shapes the Finance sub-nav around onboarding:
 *  - Until the branch is ACTIVATED, collapse the section to a single "Configure Finance" entry.
 *  - Once activated, if the guided flow is still IN_PROGRESS (the user chose "Finish for now" with optional
 *    steps left), append a short "Resume" entry with a green tag. It disappears once they finish the flow.
 *
 * The gate is the `financeActivatedAt` FLAG, not a count of how much has been configured. A derived
 * "minimum setup" rule — identity plus at least one account — opened the whole section to a branch that had
 * an account but had never allocated its opening money, so every fund sat at zero behind a full menu of
 * things that could not work. Half-configured is not configured, and only the deliberate click at the end
 * of the wizard says which it is. It is also the same flag the ledger refuses to post without, so the nav
 * and the backend now answer to one fact rather than two that can disagree.
 *
 * Applied AFTER permission + capability filtering, so it only ever rewrites a Finance section the user can see.
 * While status is loading it leaves the nav untouched (no flicker) — the route guard is the real boundary.
 */
export function useFinanceNavGate(): (sections: ChurchNavSection[]) => ChurchNavSection[] {
  const t = useTranslations("churchNav.sections.finance")
  const { workspaceId, scopeBranch } = useFinancePageState()
  const branchId = scopeBranch?.id ?? null
  const setupQuery = useFinanceSetupQuery(workspaceId ?? null, branchId)
  const progressQuery = useFinanceOnboardingProgressQuery(workspaceId ?? null, branchId)

  // Only collapse when we KNOW it isn't set up. Unknown (loading / no branch) → leave the nav as it is.
  const collapse = setupQuery.data ? !setupQuery.data.isActivated : false
  const resumable = !collapse && progressQuery.data?.status === FinanceOnboardingStatus.InProgress
  const badgeLabel = t("continueSetupBadge")

  return React.useCallback(
    (sections: ChurchNavSection[]) => {
      if (collapse) {
        return sections
          .filter((section) => section.url !== GLOBAL_FINANCE_SECTION_URL)
          .map((section) =>
            section.url === FINANCE_SECTION_URL ? { ...section, items: [CONFIGURE_ITEM] } : section,
          )
      }
      if (resumable) {
        const continueItem: ChurchNavSection["items"][number] = {
          ...CONTINUE_ITEM,
          badge: { label: badgeLabel, variant: ChurchNavBadgeVariant.Success },
        }
        return sections.map((section) =>
          section.url === FINANCE_SECTION_URL ? { ...section, items: [...section.items, continueItem] } : section,
        )
      }
      return sections
    },
    [collapse, resumable, badgeLabel],
  )
}
